import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { shouldSubmitOnEnter } from '../utils/imeSubmit';
import { useSelectionCapture } from '../hooks/useSelectionCapture';
import { useTranslation } from 'react-i18next';
import api, { sessionAPI } from '../utils/api';  // ✅ 使用api实例而非全局axios
import { resolveErrorMessage } from '../utils/errorMessage';
import { resolveFontFamily } from '../utils/fontFamilyResolver';
import AudioPlayer from './AudioPlayer';
import FuriganaText, { renderFuriganaSegments, FuriganaFragment } from './FuriganaText';
import { projectDecoratedText, interpolateIdentityRanges, IDENTITY_MARKER } from '../utils/furigana';
import { useFuriganaAria, useFurigana } from '../contexts/FuriganaContext';
import { logClientError } from '../utils/safeLog';
import { isSameScreen } from '../utils/renderStateSnapshot';
import { waitForStageWrites } from '../utils/stageWriteBarrier';

const MESSAGE_STATUS = {
  PENDING: 'pending',
  SAVED: 'saved',
  ERROR: 'error',
};

// Bilingual matching tokens (NOT user-facing — they detect intent in the
// AI/user text, which is Japanese or English depending on the active language).
// Displayed chip labels + sent values live in i18n under chat.suggest.*.
const SUGGEST_KW_DEFS = [
  { id: 'mean', keywords: ['平均', '平均値', 'mean', 'average'] },
  { id: 'median', keywords: ['中央値', 'median'] },
  { id: 'quartile', keywords: ['四分位', '箱ひげ', 'quartile', 'box plot', 'box-and-whisker', 'interquartile'] },
  { id: 'histogram', keywords: ['ヒストグラム', '度数分布', 'histogram', 'frequency distribution'] },
  { id: 'trend', keywords: ['傾向', '増加', '減少', 'trend', 'increase', 'decrease', 'rising', 'falling'] },
  { id: 'category', keywords: ['カテゴリ', '分類', '比較', 'category', 'categories', 'comparison'] },
];
// The reply candidates live in the chat footer, which does not scroll and steals height
// from the visible conversation. Two fully-readable candidates beat three truncated ones:
// a student has to read a candidate before choosing it, so we cap the count rather than
// clipping the text. Applies to both the contextual and the keyword-fallback paths.
const MAX_QUICK_SUGGESTIONS = 2;
const SUGGEST_SCATTER_KEYWORDS = ['散布図', '相関', 'プロット', 'scatter', 'correlation', 'plot'];
const STAGE_CUES = ['ステージ', 'stage', '段階', '可視', '見えている', 'ハイライト', 'visible', 'highlight', 'currently shown'];
const BAND_CUES = ['帯グラフ', 'band', '帯セグメント', 'セグメント', 'segment', 'stacked'];
const PERCENT_CUES = ['%', '％', '割合', '構成比', 'percent', 'percentage', 'proportion', 'share', 'ratio'];
const COMPARE_CUES = ['差', '大きい', '小さい', '比較', '順位', 'ランク', '最多', '最少', 'difference', 'larger', 'smaller', 'compare', 'rank', 'most', 'fewest', 'highest', 'lowest'];
const TOTAL_CUES = ['合計', 'トータル', '総', 'total', 'sum', 'overall'];
const INCREASE_CUES = ['増', '減', '推移', '変化', '伸び', '下降', '上昇', 'increase', 'decrease', 'change', 'rise', 'fall', 'grow'];

// Default suggestion chips, built from i18n (labels shown / values sent).
// Capped like every other path: a brand-new lesson is the first thing a student
// sees, so it must not fall back to the old taller three-button block.
const buildDefaultSuggestions = (t) =>
  (t('chat.suggest.default', { returnObjects: true }) || [])
    .slice(0, MAX_QUICK_SUGGESTIONS)
    .map((item, i) => ({
      id: `default-${i}`,
      label: item.label,
      value: item.value,
    }));

// ICCHP 2026 §6.5: Felder-Silverman 自動分析（analyzeLearningStyleFromHistory）は廃止されました。
// 学習スタイルは固定タイプ分類ではなく、サーバ側で観察される一時的な「呈現モード偏好」として扱います。

// Item 15, proposal C (switch ui_support_words, OFF by default): the four levels as
// words and a picture instead of "Lv 1..4", and one line saying how the tutor is
// helping right now — also when the level was raised automatically. Only the
// presentation changes: the level rules, their limits and the logs are the same.
// The line is a statement, never a question (PI ruling item 10: gentle, no asking).
// It claims only what is known (Codex J1): with no known effective level it names
// the choice alone, and when the effective level differs it names both without
// promising that a button changes the help (a static floor can keep it higher).
function SupportWordsSelector({ value, effective, onChange }) {
  const { t } = useTranslation();
  const name = (level) => t(`chat.supportWords.levels.${level}.name`);
  const known = [1, 2, 3, 4].includes(effective);
  let status;
  if (!known) {
    status = t('chat.supportWords.statusChosen', { name: name(value) });
  } else if (effective !== value) {
    status = t('chat.supportWords.statusRaised', { name: name(effective), chosen: name(value) });
  } else {
    status = t('chat.supportWords.statusSame', { name: name(value) });
  }
  const shown = known ? effective : value;
  return (
    <div className="flex flex-col gap-2" data-testid="support-words">
      <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
        <FuriganaText text={t('chat.supportWords.label')} />
      </span>
      <div className="flex flex-wrap gap-2" role="group" aria-label={t('chat.supportWords.label')}>
        {[1, 2, 3, 4].map((level) => {
          const chosen = value === level;
          const helping = !chosen && shown === level;
          return (
            <button
              key={level}
              type="button"
              onClick={() => onChange?.(level)}
              aria-pressed={chosen}
              aria-label={t('chat.supportWords.buttonAria', { name: name(level), level })}
              data-level={level}
              className="flex items-center gap-1 rounded-full px-3 py-1 text-sm font-semibold border transition-colors"
              style={chosen
                ? { background: 'var(--primary)', color: 'var(--text-on-primary)', borderColor: 'var(--primary)' }
                : {
                  background: 'var(--bg-surface)',
                  color: 'var(--text)',
                  borderColor: helping ? 'var(--primary)' : 'var(--border)',
                  borderStyle: helping ? 'dashed' : 'solid',
                }
              }
            >
              <span aria-hidden="true">{t(`chat.supportWords.levels.${level}.icon`)}</span>
              <FuriganaText text={name(level)} />
              <span className="text-xs" aria-hidden="true" style={{ opacity: 0.75 }}>
                {t('chat.supportWords.levelTag', { level })}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-xs" role="status" aria-live="polite" style={{ color: 'var(--text-muted)' }}>
        <FuriganaText text={status} />
      </p>
    </div>
  );
}

export function SupportLevelSelector({ value, onChange, words = false, effective = null }) {
  const { t } = useTranslation();
  if (words) {
    return <SupportWordsSelector value={value} effective={effective} onChange={onChange} />;
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('chat.supportLevel')} /></span>
      {[1, 2, 3, 4].map((level) => (
        <button
          key={level}
          onClick={() => onChange?.(level)}
          className="rounded-full px-3 py-1 text-xs font-semibold border transition-colors"
          style={value === level
            ? { background: 'var(--primary)', color: 'var(--text-on-primary)', borderColor: 'var(--primary)' }
            : { background: 'var(--bg-surface)', color: 'var(--text)', borderColor: 'var(--border)' }
          }
        >
          Lv {level}
        </button>
      ))}
    </div>
  );
}

function ChatInterface({
  studentProfile,
  graphId,
  settings,
  sessionId,
  graphType,
  supportLevel,
  effectiveSupportLevel,
  onSupportLevelChange,
  problemId,
  questionId,
  presetId,
  onUiActions,
  recentEvents,
  onMessageSent,
  // Part 2 PR1: what the renderer reports it actually drew. Owned by
  // InteractiveGraph and lifted through the parent — never recomputed here,
  // because a second implementation of "what is visible" is exactly how the
  // frontend and backend drifted apart in the first place.
  renderState = null,
  // Phase A research log (null unless the instrumentation flag is on):
  // (kind, trigger) for the AI-reply read-aloud. Never receives text.
  onTtsEvent = null,
  // Item 15 proposal C (UI switch ui_support_words): levels in words and pictures.
  supportWords = false,
}) {
  const { t, i18n } = useTranslation();
  const ttsEventRef = useRef(onTtsEvent);
  // Published after commit (Codex B-R7).
  useLayoutEffect(() => {
    ttsEventRef.current = onTtsEvent;
  });
  const fa = useFuriganaAria();
  const { enabled: furiganaEnabled } = useFurigana();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [userRecordingAudioUrls, setUserRecordingAudioUrls] = useState({}); // ユーザー録音音声
  const [aiResponseAudioUrls, setAiResponseAudioUrls] = useState({}); // AI応答音声
  const [highlightedText, setHighlightedText] = useState({}); // テキストハイライト
  const highlightAccentColor = useMemo(() => settings?.highlightAccentColor || '#facc15', [settings?.highlightAccentColor]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [quickSuggestions, setQuickSuggestions] = useState(() => buildDefaultSuggestions(t));
  const latestSessionIdRef = useRef(null);
  const messagesRef = useRef([]);
  // Part 2 PR1 — read through refs, not state, so the send path always sees the
  // CURRENT screen rather than whatever was captured when the handler closed
  // over its scope. A stale closure here would defeat the whole grounding check.
  const renderStateRef = useRef(null);
  const turnIdRef = useRef(null);
  const attemptRef = useRef(0);
  renderStateRef.current = renderState;
  // Identity read from LIVE props. The snapshot only reaches us after
  // InteractiveGraph's passive effect runs, so during a Dashboard graph swap
  // the parent still holds the old snapshot and an in-flight reply would
  // compare equal. Props change synchronously with the render, so they close
  // that window.
  const liveIdentityRef = useRef(null);
  liveIdentityRef.current = `${graphId ?? ''}|${sessionId ?? ''}`;
  const updateQuickSuggestions = useCallback((messageHistory = []) => {
    const defaults = buildDefaultSuggestions(t);
    const graphTypeKey = (graphType || '').toLowerCase();
    const isScatterType = /scatter|dot/.test(graphTypeKey);
    const isBandType = /band|stack/.test(graphTypeKey);
    const isCompositionType = /pie|composition|venn/.test(graphTypeKey);

    if (!messageHistory || messageHistory.length === 0) {
      setQuickSuggestions(defaults);
      return;
    }

    const lastAssistant = [...messageHistory].reverse().find((msg) => msg.role === 'assistant');
    const lastUser = [...messageHistory].reverse().find((msg) => msg.role === 'user');

    // Two buckets, because only MAX_QUICK_SUGGESTIONS of them survive: items tied to
    // THIS chart/stage/data outrank generic concept prompts. Matching order alone used
    // to decide this, which was harmless at a cap of four but silently starved the
    // specific hints once the cap dropped to two.
    const suggestions = [];
    const genericSuggestions = [];

    const pushInto = (bucket, id, label, value) => {
      const seen = (item) => item.value === value;
      if (!suggestions.some(seen) && !genericSuggestions.some(seen)) {
        bucket.push({ id, label, value });
      }
    };
    const pushSuggestion = (id, label, value) => pushInto(suggestions, id, label, value);
    const pushGenericSuggestion = (id, label, value) => pushInto(genericSuggestions, id, label, value);

    if (lastAssistant) {
      // Prefer reply candidates generated alongside the tutor's latest answer.
      // They understand the current turn; the keyword rules below remain as a
      // compatibility fallback for restored history and older backend responses.
      const contextualReplies = Array.isArray(lastAssistant.suggestedReplies)
        ? lastAssistant.suggestedReplies
            .filter((reply) => typeof reply === 'string')
            .map((reply) => reply.replace(/\s+/g, ' ').trim())
            .filter((reply, index, all) => reply && reply.length <= 120 && all.indexOf(reply) === index)
            .slice(0, MAX_QUICK_SUGGESTIONS)
        : [];
      if (contextualReplies.length > 0) {
        setQuickSuggestions(contextualReplies.map((reply, index) => ({
          id: `contextual-${index}`,
          label: reply,
          value: reply,
        })));
        return;
      }

      const text = lastAssistant.content || '';
      const normalized = text.replace(/\s+/g, '');
      const lowered = text.toLowerCase();

      // hay combines the lowercased text and the whitespace-stripped text so
      // cue matching works for both English (spaces preserved in `lowered`)
      // and Japanese (handled by either). Cues are bilingual matching tokens.
      const hay = `${lowered} ${normalized}`;
      const cueMatches = (arr) => arr.some((kw) => hay.includes(kw.toLowerCase()));

      const kwDefs = isScatterType
        ? [...SUGGEST_KW_DEFS, { id: 'scatter', keywords: SUGGEST_SCATTER_KEYWORDS }]
        : SUGGEST_KW_DEFS;
      kwDefs.forEach((entry) => {
        if (cueMatches(entry.keywords)) {
          const items = t(`chat.suggest.kw.${entry.id}`, { returnObjects: true }) || [];
          items.forEach((item, index) => {
            pushGenericSuggestion(`${entry.id}-${index}`, item.label, item.value);
          });
        }
      });

      // Stage / visible-element follow-ups
      if (cueMatches(STAGE_CUES)) {
        (t('chat.suggest.stage', { returnObjects: true }) || []).forEach((item, index) =>
          pushSuggestion(`stage-${index}`, item.label, item.value));
      }

      // Chart-type-specific hints (e.g. strip bar graphs)
      if (isBandType || cueMatches(BAND_CUES)) {
        (t('chat.suggest.band', { returnObjects: true }) || []).forEach((item, index) =>
          pushSuggestion(`band-${index}`, item.label, item.value));
      }

      // Data-driven follow-up variations
      const hasPercent = cueMatches(PERCENT_CUES);
      const hasYear = /(19|20)\d{2}/.test(text);
      const hasCompareCue = cueMatches(COMPARE_CUES);
      const hasTotalCue = cueMatches(TOTAL_CUES);
      const hasIncreaseCue = cueMatches(INCREASE_CUES);

      const pushItems = (key, prefix) => {
        (t(key, { returnObjects: true }) || []).forEach((item, index) =>
          pushSuggestion(`${prefix}-${index}`, item.label, item.value));
      };
      const pushItem = (key, id) => {
        const item = t(key, { returnObjects: true });
        if (item && item.label) pushSuggestion(id, item.label, item.value);
      };

      if (hasPercent) pushItems('chat.suggest.ratio', 'ratio');
      if (isCompositionType) pushItem('chat.suggest.composition', 'composition-read');
      if (hasYear || hasIncreaseCue) pushItems('chat.suggest.trend2', 'trend2');
      if (hasCompareCue) pushItem('chat.suggest.compare', 'compare-top-bottom');
      if (hasTotalCue) pushItem('chat.suggest.total', 'total-check');
      if (/\d/.test(text)) pushItem('chat.suggest.number', 'number-context');
    }

    if (suggestions.length === 0 && genericSuggestions.length === 0 && lastUser?.content) {
      const userText = lastUser.content;
      pushSuggestion('clarify', t('chat.suggest.clarify.label'), t('chat.suggest.clarify.value', { text: userText }));
      const other = t('chat.suggest.other', { returnObjects: true });
      if (other && other.label) pushSuggestion('other', other.label, other.value);
    }

    // Chart/stage/data-specific first, generic concept prompts only as filler.
    const finalList = [...suggestions, ...genericSuggestions];
    defaults.forEach((item) => {
      if (finalList.length < MAX_QUICK_SUGGESTIONS && !finalList.some((entry) => entry.value === item.value)) {
        finalList.push(item);
      }
    });
    setQuickSuggestions(finalList.slice(0, MAX_QUICK_SUGGESTIONS));
  }, [setQuickSuggestions, graphType, t]);
  const buildGreetingMessage = useCallback(() => {
    if (!studentProfile) {
      return null;
    }
    const identity = studentProfile.name || studentProfile.username || '';
    const name = identity || t('chat.studentFallback');
    return {
      id: null,
      role: 'assistant',
      content: t('chat.greeting', { name }),
      // for the identity-aware render: the SAME greeting with a MARKER in the name slot(s), so the name can be
      // bared by its true interpolation position rather than string-search (which would break chrome, R21 #4).
      contentMarked: identity ? t('chat.greeting', { name: IDENTITY_MARKER }) : null,
      timestamp: new Date(),
      status: MESSAGE_STATUS.SAVED,
      persistence: null,
      isGreeting: true,
      identityName: identity || null, // the participant's real name — bared at render (never-guess, Codex R20 #2)
    };
  }, [studentProfile, t]);
  // ICCHP 2026 §6.5: 自動学習スタイル分析（analyzeLearningStyleFromHistory）
  // および関連 state（isAnalyzingLearningStyleRef, lastAnalysisAttemptRef,
  // insufficientHistoryNotifiedRef, pendingAnalysisRef）はすべて削除されました。
  // 固定タイプ分類は廃止され、呈現モード偏好はサーバ側で必要時に推論されます。
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  // 自动滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    updateQuickSuggestions(messages);
  }, [messages, updateQuickSuggestions]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      // 録音ストリームをクリーンアップ
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // 実際のマイクデバイスを選択（仮想デバイスを避ける）
  const selectRealMicrophone = async () => {
    try {
      // まず権限を取得
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      tempStream.getTracks().forEach(track => track.stop());

      // デバイス一覧を取得
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');

      console.log('🎤 利用可能な音声入力デバイス:', audioInputs.map(d => d.label));

      // 仮想デバイスを除外（BlackHole, Zoom, Virtual等）
      const realMicrophones = audioInputs.filter(device => {
        const label = device.label.toLowerCase();
        return !label.includes('blackhole') &&
               !label.includes('zoom') &&
               !label.includes('virtual') &&
               !label.includes('デフォルト') &&
               !label.includes('default');
      });

      if (realMicrophones.length > 0) {
        const selectedDevice = realMicrophones[0];
        console.log(`✅ 選択されたマイク: ${selectedDevice.label}`);
        return selectedDevice.deviceId;
      } else {
        console.warn('⚠️ 実際のマイクが見つかりません。デフォルトを使用します。');
        return null; // デフォルトデバイスを使用
      }
    } catch (error) {
      logClientError('マイク選択エラー:', error);
      return null;
    }
  };

  // 音声入力: 録音 → Whisper API で文字起こし
  const toggleVoiceInput = async () => {
    if (!settings.voiceInputEnabled) {
      alert(t('chat.alert.voiceInputDisabled'));
      return;
    }

    if (isRecording) {
      // 録音停止
      console.log('⏹️ 録音停止');
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
    } else {
      // 録音開始
      try {
        console.log('🎤 録音開始');

        // 実際のマイクを選択
        const deviceId = await selectRealMicrophone();

        const audioConstraints = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000
        };

        // デバイスIDが取得できた場合は指定
        if (deviceId) {
          audioConstraints.deviceId = { exact: deviceId };
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints
        });

        streamRef.current = stream;
        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: 'audio/webm;codecs=opus'
        });
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorderRef.current.onstop = async () => {
          console.log('📝 録音処理中...');

          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          console.log(`📊 録音サイズ: ${audioBlob.size} bytes`);

          if (audioBlob.size < 2000) {
            alert(t('chat.alert.recordingTooShort'));
            if (streamRef.current) {
              streamRef.current.getTracks().forEach(track => track.stop());
            }
            return;
          }

          // ユーザー録音URLを保存（後で表示用）
          const userAudioUrl = URL.createObjectURL(audioBlob);
          const pendingUserMessageIndex = messages.length;

          // Whisper APIで文字起こし
          try {
            console.log('📤 Whisper APIにアップロード中...');
            const formData = new FormData();
            formData.append('file', audioBlob, 'recording.webm');

            const response = await api.post('/api/speech/transcribe', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 30000
            });

            console.log('✅ 文字起こし成功:', response.data.text);
            setInputText(response.data.text);

            // ユーザー録音を保存
            setUserRecordingAudioUrls(prev => ({
              ...prev,
              [pendingUserMessageIndex]: userAudioUrl
            }));

          } catch (error) {
            logClientError('❌ 文字起こしエラー:', error);
            // Backend speech errors carry a {code} now; resolve to a localized
            // message (errors.speech_recognition_failed) instead of interpolating
            // the raw Japanese detail into the alert.
            alert(resolveErrorMessage(error, t));
          }

          // ストリームクリーンアップ
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);

      } catch (error) {
        logClientError('❌ マイク取得エラー:', error);
        alert(t('chat.alert.micError', { message: error.message }));
      }
    }
  };

  // AI応答音声生成（Google Cloud TTS）
  // reporter: the speech-event callback of the task this reply belongs to, captured when the
  // send started (Codex B-R8); defaults to the current one for other callers.
  const generateAIResponseAudio = async (text, messageIndex, reporter = ttsEventRef.current) => {
    if (!settings?.voiceOutputEnabled) {
      console.log('⚠️ 音声出力が無効です');
      return null;
    }

    if (!text || text.trim().length === 0) {
      console.warn('⚠️ 音声化するテキストがありません');
      return null;
    }

    let reportTts = null;
    try {
      console.log(`🔊 AI音声生成開始 (index: ${messageIndex})`);
      console.log(`📝 テキスト: "${text.substring(0, 100)}..."`);

      // Captured at the start; success / error go through the same reporter (Codex B-R7).
      reportTts = reporter;
      if (reportTts) reportTts('tts_start', 'ai_reply');
      const response = await api.post('/api/tts/synthesize', {
        text: text,
        voice_type: settings.voiceType || 'female_neural',
        speaking_rate: settings.speechRate || 1.0,
        pitch: 0.0,
        volume_gain_db: 0
      }, {
        timeout: 30000
      });

      if (!response.data || !response.data.audio_content) {
        throw new Error('音声データが返されませんでした');
      }

      const audioBlob = base64ToBlob(response.data.audio_content, 'audio/mp3');
      const audioUrl = URL.createObjectURL(audioBlob);

      console.log(`✅ AI音声生成完了 (index: ${messageIndex})`);
      if (reportTts) reportTts('tts_success', 'ai_reply');
      return audioUrl;

    } catch (error) {
      if (reportTts) reportTts('tts_error', 'ai_reply');
      logClientError(`❌ AI音声生成エラー (index: ${messageIndex}):`, error);
      console.error('詳細:', error.response?.data || error.message);
      return null;
    }
  };

  // 辅助函数：将base64转换为Blob
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  // 为消息生成音频URL
  // テキストハイライト機能 - 選択範囲を記録
  const formatTimestamp = useCallback((value) => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString(i18n.language === 'ja' ? 'ja-JP' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  }, [i18n.language]);

  // The message list is one surface with many messages, so a single capture
  // resolves the index from the DOM instead of mounting a listener per bubble —
  // a listener per message would all fire on the same document event and then
  // race to claim the selection.
  const messageLogRef = useRef(null);
  useSelectionCapture(messageLogRef, useCallback(() => {
    const selection = window.getSelection && window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    let node = selection.getRangeAt(0).commonAncestorContainer;
    if (node && node.nodeType !== 1) node = node.parentElement;
    const bubble = node && node.closest && node.closest('[data-message-index]');
    if (!bubble) return;
    const idx = Number(bubble.getAttribute('data-message-index'));
    if (Number.isInteger(idx)) handleTextSelectionRef.current(idx);
  }, []));

  const handleTextSelection = (messageIndex) => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    if (selectedText.length > 0) {
      // Event only, never the text. Chat bubbles carry minors' own free writing,
      // and this path now runs on the tablets the study actually uses — the
      // mouse-only wiring had kept it off those devices by accident.
      console.log('✨ テキストをハイライト');

      // 既存のハイライトに追加
      setHighlightedText(prev => {
        const existing = prev[messageIndex] || [];

        // 🔍 重複チェック: 選択されたテキストが既にハイライトされているか確認
        const isAlreadyHighlighted = existing.some(existingText => {
          // 完全一致の場合
          if (existingText === selectedText) {
            return true;
          }
          // 既存のハイライトが選択テキストに含まれている場合
          if (selectedText.includes(existingText)) {
            return true;
          }
          // 選択テキストが既存のハイライトに含まれている場合
          if (existingText.includes(selectedText)) {
            return true;
          }
          return false;
        });

        if (isAlreadyHighlighted) {
          console.log('⚠️ このテキストは既にハイライトされています');
          return prev;
        }

        console.log('✅ 新しいハイライトを追加');
        return {
          ...prev,
          [messageIndex]: [...existing, selectedText]
        };
      });
    }
  };

  // 特定のハイライトを解除
  const removeHighlight = (messageIndex, text) => {
    setHighlightedText(prev => {
      const existing = prev[messageIndex] || [];
      const filtered = existing.filter(t => t !== text);

      if (filtered.length === 0) {
        const newHighlights = { ...prev };
        delete newHighlights[messageIndex];
        return newHighlights;
      }

      return {
        ...prev,
        [messageIndex]: filtered
      };
    });
  };

  // すべてのハイライトを解除
  const clearAllHighlights = (messageIndex) => {
    setHighlightedText(prev => {
      const newHighlights = { ...prev };
      delete newHighlights[messageIndex];
      return newHighlights;
    });
  };

  // Render a chat message body: strip **bold**, apply the AI's highlight phrases, and add furigana ruby. The
  // whole plain text is annotated ONCE and its segments are projected onto the bold/highlight ranges (shared
  // projectDecoratedText) — a bold/highlight boundary that isolates a context-sensitive kanji must never be
  // re-tokenized in isolation (箱の**中** would read a whole-word ちゅう instead of contextual なか). Codex R15
  // #4 / R16 #2. assistant / participant 双方のメッセージ本文に付与。
  const renderHighlightedText = (rawText, highlights, furigana = false, identityRanges = []) => {
    if (!rawText) return rawText;
    // furigana ruby applies only when the caller asks AND the global toggle is on (renderFuriganaSegments,
    // unlike <FuriganaText>, does not read the toggle — gate it here before projecting with ruby).
    const ruby = furigana && furiganaEnabled;
    return projectDecoratedText(rawText, highlights, ruby, identityRanges).map((g, i) => {
      const plain = g.segs.map((s) => (s.base != null ? s.base : s.text)).join('');
      let node = ruby && !(g.meta && g.meta.isIdentity)
        ? renderFuriganaSegments(g.segs, `mb${i}`)
        : plain;
      if (g.meta && g.meta.hl) {
        node = (
          <span key={`h${i}`} style={{ backgroundColor: `${highlightAccentColor}33`, boxShadow: `0 0 0 2px ${highlightAccentColor}55`, padding: '2px 0' }}>{node}</span>
        );
      }
      if (g.meta && g.meta.bold) node = <strong key={`b${i}`}>{node}</strong>;
      return <React.Fragment key={`mb${i}`}>{node}</React.Fragment>;
    });
  };

  // Render the greeting (whose template interpolates the participant's NAME) with the name bared — a proper
  // name's reading is not spelling-derivable (小鳥遊 ≠ ことり), so it is never furigana-guessed (Codex R20 #2) —
  // while the surrounding chrome keeps BOTH its furigana AND its bold/highlight decoration (Codex R21 #2, via
  // the highlights passed through). `marked` is the greeting with IDENTITY_MARKER in the name slot(s), so the
  // split follows the true interpolation position and never collides with chrome (Codex R21 #4).
  const getIdentityProjection = (marked, identity) => interpolateIdentityRanges(marked, identity);

  const renderMessageWithBareIdentity = (marked, identity, highlights) => {
    const projection = getIdentityProjection(marked, identity);
    return renderHighlightedText(projection.text, highlights, true, projection.identityRanges);
  };

  // セッションごとの履歴を読み込み
  useEffect(() => {
    let isActive = true;
    latestSessionIdRef.current = sessionId ?? null;

    const resetSessionScopedState = () => {
      setUserRecordingAudioUrls({});
      setAiResponseAudioUrls({});
      setHighlightedText({});
      updateQuickSuggestions([]);
      setMessages([]);
      setNotice(null);
      // ICCHP §6.5: learning-style analysis state was removed.
    };

    const applyMessagesToState = (sessionMessages) => {
      if (!isActive || latestSessionIdRef.current !== sessionId) {
        return;
      }
      setMessages(sessionMessages);
    };

    const loadSessionHistory = async () => {
      const greeting = buildGreetingMessage();
      if (!sessionId) {
        applyMessagesToState(greeting ? [greeting] : []);
        setIsHistoryLoading(false);
        setLoadError(null);
        return;
      }

      latestSessionIdRef.current = sessionId;
      setIsHistoryLoading(true);
      setLoadError(null);

      try {
        const response = await sessionAPI.getMessages(sessionId);
        const serverMessages = (response.data || []).map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
          audio_url: msg.audio_url || null,
          status: MESSAGE_STATUS.SAVED,
          persistence: null,
        }));

        if (serverMessages.length > 0) {
          applyMessagesToState(serverMessages);
        } else {
          applyMessagesToState(greeting ? [greeting] : []);
        }
      } catch (error) {
        logClientError('Failed to load session messages:', error);
        setLoadError(error);
        applyMessagesToState(greeting ? [greeting] : []);
      } finally {
        if (isActive && latestSessionIdRef.current === sessionId) {
          setIsHistoryLoading(false);
        }
      }
    };

    resetSessionScopedState();
    loadSessionHistory();

    return () => {
      isActive = false;
    };
  }, [sessionId, buildGreetingMessage, updateQuickSuggestions]);

  // ICCHP 2026 §6.5: 既存の学習スタイル読み込み + 自動分析 useEffect は削除されました。
  // 固定タイプ分類は廃止。呈現モード偏好はサーバ側で必要時に推論されます。

  // 发送消息
  const handleSend = async (overrideText, opts = {}) => {
    // Captured before the first await: the reply's speech events belong to the task this send
    // started in, even if the student switches task while the messages are saved (Codex B-R8).
    const reportTtsForThisSend = ttsEventRef.current;
    // A canned request (e.g. the「わからない」button) passes its text directly; a
    // normal send reads the input box. Only a real string counts as an override,
    // so existing callers that pass a click event fall through to inputText.
    const isOverride = typeof overrideText === 'string';
    // F2 (PI-ratified): the「わからない」button is a SYSTEM-generated assistance request, not
    // participant dialogue — it is excluded from re-question / category / adaptation + the
    // backend research trajectory (via the assistance_request flag below).
    const isAssistanceRequest = opts.assistanceRequest === true;
    const trimmed = (isOverride ? overrideText : inputText).trim();
    if (!trimmed || isTyping) {
      return;
    }

    if (!sessionId) {
      alert(t('chat.alert.noSession'));
      return;
    }

    // Fixed BEFORE the question is saved, so both saves of this turn carry it
    // (tutor history v2): the server stores a confirmed turn itself at the ACK, and
    // a save with the same turn id — this one, its retry, the reply's — returns the
    // row already stored instead of storing it twice.
    // Collision-resistant: index+length repeats across reloads and tabs, and
    // a repeated turn_id would be read as a resend of a DIFFERENT turn.
    const turnId =
      turnIdRef.current ||
      `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    turnIdRef.current = turnId;

    const userMessageIndex = messages.length;
    const userMessageTimestamp = new Date();
    const userMessage = {
      id: null,
      role: 'user',
      content: trimmed,
      timestamp: userMessageTimestamp,
      status: MESSAGE_STATUS.PENDING,
      persistence: {
        role: 'user',
        content: trimmed,
        audio_url: null,
        turn_id: turnId
      }
    };

    setMessages((prev) => [...prev, userMessage]);
    if (!isOverride) setInputText(''); // preserve any typed draft when sending a canned request
    setIsTyping(true);

    // Part 2 PR1: has this turn's server-side hold been settled (committed or
    // explicitly discarded)? While it is false the server may still be holding
    // a staged turn that blocks every later turn for this student, so the
    // cleanup in `finally` releases it.
    let turnResolved = false;
    // Did the student's message reach the Messages table? A cold restore
    // rebuilds conversation_history from those rows, so if we abandon the turn
    // the server has to keep the user half to match — otherwise a restart
    // replays a question the live process dropped.
    let userTurnPersisted = false;
    let persistedUserMessageId = null;

    // Phase 2: notify parent of sent message for re-question tracking.
    // F2: an assistance request is a system-generated help ask, not participant dialogue — skip
    // re-question / question-category / adaptation-event tracking (and don't let its fixed text
    // pollute the parent's word-overlap re-question detector).
    if (onMessageSent && !isAssistanceRequest) {
      onMessageSent(trimmed);
    }

    try {
      console.log('📤 メッセージ送信:', trimmed);

      // 永続化: ユーザーメッセージ
      // F2: an assistance request is UI-only — never persisted as a participant Message. Persisting
      // it would (a) classify its fixed「小さい」text as a question category and (b) after a cold
      // restore re-enter conversation_history so the next save_trajectory re-pollutes trajectory /
      // quality / turn counts. Show it locally (marked saved, no server id) and skip persistence.
      if (isAssistanceRequest) {
        setMessages((prev) =>
          prev.map((msg, idx) =>
            idx === userMessageIndex
              ? { ...msg, status: MESSAGE_STATUS.SAVED, persistence: null }
              : msg
          )
        );
      } else {
        try {
          // Scope the stored turn to the sub-question the student is actually on.
          // Without this the messages.question_key column is NULL for every row in
          // production and per-question analysis has nothing to group by.
          const userPersistPayload = {
            role: 'user',
            content: trimmed,
            audio_url: null,
            turn_id: turnId
          };
          // Send whatever id we already hold (the same one the /api/chat request
          // carries) — the SERVER resolves it to the canonical Question.id scoped
          // to this session, or to NULL. Never build a composite key here: a
          // client-side guess is unverifiable and can mis-attribute a turn.
          if (questionId) {
            // Omitted, not null, when nothing is active: an absent key means
            // "the client had no scope to report", which the server already
            // treats as NULL without having to parse a sentinel.
            userPersistPayload.question_id = questionId;
          }
          const persistedUser = await sessionAPI.addMessage(sessionId, userPersistPayload);

          if (persistedUser?.data) {
            const saved = persistedUser.data;
            userTurnPersisted = true;
            persistedUserMessageId = Number.isInteger(saved.id) ? saved.id : null;
            setMessages((prev) =>
              prev.map((msg, idx) =>
                idx === userMessageIndex
                  ? {
                      ...msg,
                      id: saved.id,
                      timestamp: saved.created_at ? new Date(saved.created_at) : msg.timestamp,
                      status: MESSAGE_STATUS.SAVED,
                      persistence: null
                    }
                  : msg
              )
            );
            setNotice((prevNotice) =>
              prevNotice && prevNotice.type === 'user-save-error' ? null : prevNotice
            );
          } else {
            throw new Error('保存に失敗しました');
          }
        } catch (persistError) {
          logClientError('ユーザーメッセージの保存に失敗しました:', persistError);
          setMessages((prev) =>
            prev.map((msg, idx) =>
              idx === userMessageIndex
                ? {
                    ...msg,
                    status: MESSAGE_STATUS.ERROR
                  }
                : msg
            )
          );
          setNotice({
            type: 'user-save-error',
            text: t('chat.noticeUserSaveRetry')
          });
        }
      }

      const chatPayload = {
        student_id: studentProfile?.student_id || studentProfile?.id || 'default',
        message: trimmed,
        graph_id: graphId,
        language: i18n.language,
      };
      const supportLevelForRequest = Number.isFinite(effectiveSupportLevel)
        ? effectiveSupportLevel
        : supportLevel;
      if (Number.isFinite(supportLevelForRequest)) {
        chatPayload.support_level = supportLevelForRequest;
      }
      if (problemId) {
        chatPayload.problem_id = problemId;
      }
      if (questionId) {
        chatPayload.question_id = questionId;
      }
      // Pass DB session ID for trajectory recording (PACE Formula 3)
      if (sessionId) {
        chatPayload.session_id = sessionId;
      }
      // Phase 2: attach recent interaction events for cognitive adaptation
      if (recentEvents && recentEvents.length > 0) {
        chatPayload.recent_events = recentEvents;
      }
      // F2: flag the system-generated「わからない」assistance request so the backend excludes it
      // from the research trajectory / quality / scoring / turn counts.
      if (isAssistanceRequest) {
        chatPayload.assistance_request = true;
      }
      // Pass preset ID for teacher workspace mode
      if (presetId) {
        chatPayload.preset_id = presetId;
      }

      // Part 2 PR1: report what the renderer actually drew, and tag this turn so
      // the server can grant exactly one retry if the student navigates away
      // mid-generation. Both fields are optional server-side.
      const sentSnapshot = renderStateRef.current;
      const sentIdentity = liveIdentityRef.current;
      if (sentSnapshot) {
        chatPayload.render_state = sentSnapshot;
      }
      chatPayload.turn_id = turnId;
      chatPayload.attempt = attemptRef.current || 0;
      // Tell the server the question is already a row in Messages. The ledger
      // holds this for the whole turn, so an abandonment can restore it even
      // after a stale transition has cleared the staged slice.
      chatPayload.user_turn_persisted = userTurnPersisted;
      // Which Messages row this turn's question is: lets the server tell a cache
      // rebuild's echo of THIS message from an earlier message with the same text.
      if (persistedUserMessageId != null) {
        chatPayload.persisted_message_id = persistedUserMessageId;
      }

      let response = await api.post('/api/chat', chatPayload);

      // ── Grounding gate — runs BEFORE any side effect ─────────────────────
      // The reply must be checked against the screen the student is on NOW,
      // before ui_actions are applied, before the text is shown and before the
      // candidates update. Applying first and checking after would land an
      // action on a view they already left.
      //
      // `groundedOn` tracks the snapshot the CURRENT response was generated
      // from. It must be re-pointed on retry: comparing a successful attempt=1
      // reply against the ORIGINAL snapshot discards it unconditionally
      // (the stage moved, which is why we retried), so the retry could never
      // deliver anything.
      let groundedOn = sentSnapshot;
      if (response.data?.response_kind === 'stale') {
        // The server saw the stage change. It cannot regenerate on its own —
        // the post-navigation view (manual show/hide, trig mode, zoom) lives
        // only in this browser — so we resubmit ONCE under the same turn_id
        // carrying a fresh snapshot.
        if ((attemptRef.current || 0) === 0) {
          attemptRef.current = 1;
          // The server's stage disagreed with ours — which is usually OUR OWN
          // stage write still in flight, since a jump applies to the view
          // immediately and persists afterwards. Retrying into that window
          // loses the race, the server sees the same mismatch, and the turn
          // ends `neutral_final`: one retry spent, no answer. Bounded wait, so
          // a hung write delays the retry instead of blocking the chat.
          await waitForStageWrites();
          const freshSnapshot = renderStateRef.current;
          const retryPayload = { ...chatPayload, attempt: 1 };
          if (freshSnapshot) retryPayload.render_state = freshSnapshot;
          groundedOn = freshSnapshot || sentSnapshot;
          response = await api.post('/api/chat', retryPayload);
        }
      }
      // A reply grounded on a screen we are no longer showing is dropped
      // WHOLESALE — text, actions and candidates together. Dropping only the
      // candidates is not enough: the keyword fallback would then re-derive
      // candidates from the stale reply text, so nothing stale is really gone.
      //
      // The comparison is snapshot-vs-snapshot rather than token-vs-token: the
      // server's grounding_token is a hash over ITS canonical serialization, so
      // the client cannot reproduce it without duplicating that logic — and a
      // second implementation of the same rule is what caused the original
      // front/back divergence. The token still travels for tracing.
      // ACK-gated commit: until we confirm, the server holds this turn OUT of
      // the model's history and has NOT written its trajectory row. So a reply
      // we never show simply never existed — we just have to say so.
      // Returns true when the server has actually committed (or when there is
      // nothing to commit). A FAILED positive ACK is not cosmetic: the server
      // still holds the turn outside history and trajectory until it expires,
      // so showing the reply anyway would mean the student saw an answer that
      // later generations never see and the research record never gets.
      const ackTurn = async (accepted) => {
        if (!response.data?.requires_ack || !sessionId) return true;
        // Retried once with the SAME turn_id: if the first ACK committed but
        // its response was lost, the server replays success rather than 409ing,
        // so this recovers instead of withholding a committed answer.
        for (let i = 0; i < 2; i += 1) {
          try {
            await api.post('/api/chat/ack', { session_id: sessionId, turn_id: turnId, accepted });
            return true;
          } catch (ackError) {
            if (i === 1) {
              logClientError('chat ack failed', ackError);
              return false;
            }
          }
        }
        return false;
      };

      // Identity first, from live props — this catches a graph/session swap
      // even before the new snapshot has propagated.
      const identityChanged = liveIdentityRef.current !== sentIdentity;
      const screenNow = renderStateRef.current;
      if (identityChanged || (groundedOn && screenNow && !isSameScreen(groundedOn, screenNow))) {
        // Only WE can see this class of change (manual show/hide, trig mode
        // never reach the server), so a silent local discard is not enough:
        // the reply is already in the server's conversation history and would
        // keep steering later turns even though the student never saw it.
        // Tell the server to roll it back, and take our one retry against the
        // screen actually in front of the student.
        // Tell the server we never showed it. Nothing was committed, so this
        // DISCARDS rather than rolls back — no history truncation, no
        // trajectory row to undo.
        const rejected = await ackTurn(false);
        // If the rejection never reached the server the turn is still staged,
        // so a retry would be refused by the session barrier. Stop instead of
        // spending the one retry on a request that cannot succeed — and leave
        // `turnResolved` false so the cleanup tries the release again.
        if (!rejected) return;
        turnResolved = true;
        // A graph/session swap is not a stale-screen retry: the whole request
        // (graph_id, problem, question) belongs to the graph the student has
        // left. Re-sending it would answer about the old graph and attach it to
        // the new one. Let the student ask again in the new context.
        if (identityChanged) return;
        if ((attemptRef.current || 0) === 0) {
          attemptRef.current = 1;
          const retryPayload = {
            ...chatPayload,
            attempt: 1,
            ...(screenNow ? { render_state: screenNow } : {}),
          };
          try {
            response = await api.post('/api/chat', retryPayload);
            groundedOn = screenNow;
          } catch (retryError) {
            logClientError('chat stale retry failed', retryError);
            return;
          }
          // Moved again during the retry: reject that one too and stop. One
          // retry is the whole budget on both sides, and because nothing is
          // committed until ACK, stopping here leaves no residue.
          if (!isSameScreen(groundedOn, renderStateRef.current)) {
            turnResolved = await ackTurn(false);
            return;
          }
        } else {
          return;
        }
      }
      // Commit BEFORE delivering. If the commit did not land, do not show the
      // reply: the server would drop it at TTL and the student would be the
      // only party that ever saw it.
      const committed = await ackTurn(true);
      turnResolved = committed;
      if (!committed) {
        // Shape matters: the banner renders `notice.text`, so a bare string
        // would show an empty box and tell the student nothing.
        setNotice({ type: 'tutor-commit-failed', text: t('chat.noticeCommitFailed') });
        return;
      }
      // Awaiting the ACK opens a fresh window: the student may have navigated
      // or switched graphs while it was in flight. The turn IS committed now —
      // it is in history and the trajectory — so we must still SHOW and PERSIST
      // it. Returning here instead left Messages holding only the question
      // while live history and the research record held an answer nobody saw,
      // and a cold restore then dropped that answer again.
      //
      // Only the ACTIONS are screen-dependent, so only the actions are skipped:
      // the reply text is about the data, not about which elements happen to be
      // revealed right now.
      const screenStillMatches = !(
        liveIdentityRef.current !== sentIdentity ||
        (groundedOn && renderStateRef.current && !isSameScreen(groundedOn, renderStateRef.current))
      );

      const assistantMessageIndex = userMessageIndex + 1;
      const assistantContent = response.data?.response ?? t('chat.assistantFallback');
      console.log('✅ AI応答受信:', assistantContent.substring(0, 100) + '...');

      // Phase 2: forward ui_actions from chat response to overlay system.
      // F2: an assistance response's ui_actions must NOT be applied/logged — the parent persists
      // each linkage action as a participant-linked ActionLog row that research_export counts.
      // Part 2 PR1: reached only after the grounding gate above.
      const uiActions = response.data?.ui_actions;
      if (
        screenStillMatches &&
        uiActions && Array.isArray(uiActions) && uiActions.length > 0 &&
        onUiActions && !isAssistanceRequest
      ) {
        onUiActions(uiActions);
      }

      const assistantMessage = {
        id: null,
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date(),
        status: MESSAGE_STATUS.PENDING,
        persistence: {
          role: 'assistant',
          content: assistantContent,
          audio_url: null,
          turn_id: turnId
        },
        metadata: {
          learning_style: response.data.learning_style,
          strategy: response.data.strategy,
          turn: response.data.conversation_turn
        },
        suggestedReplies: Array.isArray(response.data?.suggested_replies)
          ? response.data.suggested_replies
          : null,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      // ICCHP §6.5: pendingAnalysisRef.current 設定を削除（自動 LS 分析は廃止）

      // F2: the assistance hint is UI-only too — do not persist it as a participant Message (it
      // would re-enter conversation_history on cold restore and pollute the research trajectory).
      if (isAssistanceRequest) {
        setMessages((prev) =>
          prev.map((msg, idx) =>
            idx === assistantMessageIndex
              ? { ...msg, status: MESSAGE_STATUS.SAVED, persistence: null }
              : msg
          )
        );
      } else {
        try {
          const persistedAssistant = await sessionAPI.addMessage(sessionId, {
            role: 'assistant',
            content: assistantContent,
            audio_url: null,
            turn_id: turnId
          });

          if (persistedAssistant?.data) {
            const savedAssistant = persistedAssistant.data;
            setMessages((prev) =>
              prev.map((msg, idx) =>
                idx === assistantMessageIndex
                  ? {
                      ...msg,
                      id: savedAssistant.id,
                      timestamp: savedAssistant.created_at ? new Date(savedAssistant.created_at) : msg.timestamp,
                      status: MESSAGE_STATUS.SAVED,
                      persistence: null
                    }
                  : msg
              )
            );
            setNotice((prevNotice) =>
              prevNotice && prevNotice.type === 'assistant-save-error' ? null : prevNotice
            );
          } else {
            throw new Error('保存に失敗しました');
          }
        } catch (persistError) {
          logClientError('アシスタントメッセージの保存に失敗しました:', persistError);
          setMessages((prev) =>
            prev.map((msg, idx) =>
              idx === assistantMessageIndex
                ? {
                    ...msg,
                    status: MESSAGE_STATUS.ERROR
                  }
                : msg
            )
          );
          setNotice({
            type: 'assistant-save-error',
            text: t('chat.noticeAssistantSaveRetry')
          });
        }
      }

      const audioUrl = await generateAIResponseAudio(assistantContent, assistantMessageIndex, reportTtsForThisSend);

      if (audioUrl) {
        setAiResponseAudioUrls((prev) => ({
          ...prev,
          [assistantMessageIndex]: audioUrl
        }));
      }

      // ICCHP §6.5: analyzeLearningStyleFromHistory() 呼出を削除（自動 LS 分析は廃止）
    } catch (error) {
      logClientError('❌ メッセージ送信エラー:', error);
      // The server keeps every open lesson up to a cap and refuses a new one past it
      // (tutor history v2). Retrying cannot help, so say what happened and whom to tell.
      const refusedDetail = error?.response?.status === 409 ? error.response.data?.detail : null;
      const slotsFull = refusedDetail && refusedDetail.code === 'lesson_slots_full';
      setMessages((prev) => [
        ...prev.slice(0, userMessageIndex + 1),
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: slotsFull
            ? t('errors.lesson_slots_full', { limit: refusedDetail.limit })
            : t('chat.errorMessage'),
          timestamp: new Date(),
          isError: true
        }
      ]);
    } finally {
      setIsTyping(false);
      // Part 2 PR1: ALWAYS release the turn refs, including on the error path.
      // If a request completed server-side but its response was lost to a
      // network error, leaving turn_id set makes the NEXT (different) message
      // reuse it; the server sees a terminal entry with a different input
      // digest and answers 409 forever, wedging this chat until a reload.
      const orphanedTurnId = turnIdRef.current;
      turnIdRef.current = null;
      attemptRef.current = 0;
      // Clearing our own refs is not enough. If the turn was staged server-side
      // and we never saw the response, it stays AWAITING_ACK and the per-user
      // barrier refuses EVERY later turn — in every tab — until it expires. So
      // tell the server we never showed it. Nothing was committed, so this
      // simply discards it. Best-effort and deliberately not awaited: the send
      // has already finished, and if this request fails too the staged-turn
      // expiry is the backstop.
      // Nothing in here may throw: this runs in `finally`, so an exception
      // would REPLACE the real send error the catch block just reported.
      if (orphanedTurnId && !turnResolved && sessionId) {
        try {
          const release = api.post('/api/chat/ack', {
            session_id: sessionId,
            turn_id: orphanedTurnId,
            accepted: false,
            // Giving up, not retrying — so the server keeps the student's
            // question if we already persisted it, and drops only the answer
            // they never saw.
            abandoned: true
          });
          if (release && typeof release.catch === 'function') release.catch(() => {});
        } catch (releaseError) {
          // Best effort by design — the staged-turn expiry is the backstop.
        }
      }
    }
  };

  const retryPersistence = useCallback(async (messageIndex) => {
    const targetMessage = messagesRef.current[messageIndex];
    if (
      !targetMessage ||
      targetMessage.status !== MESSAGE_STATUS.ERROR ||
      !targetMessage.persistence ||
      !sessionId
    ) {
      return;
    }

    setMessages((prev) =>
      prev.map((msg, idx) =>
        idx === messageIndex
          ? {
              ...msg,
              status: MESSAGE_STATUS.PENDING
            }
          : msg
      )
    );

    try {
      const persisted = await sessionAPI.addMessage(sessionId, targetMessage.persistence);
      if (!persisted?.data) {
        throw new Error('保存に失敗しました');
      }

      const saved = persisted.data;
      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === messageIndex
            ? {
                ...msg,
                id: saved.id,
                timestamp: saved.created_at ? new Date(saved.created_at) : msg.timestamp,
                status: MESSAGE_STATUS.SAVED,
                persistence: null
              }
            : msg
        )
      );

      setNotice((prevNotice) => {
        if (!prevNotice) return null;
        const errorTypesToClear = ['user-save-error', 'assistant-save-error'];
        return errorTypesToClear.includes(prevNotice.type) ? null : prevNotice;
      });
    } catch (error) {
      logClientError('メッセージ再保存に失敗しました:', error);
      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === messageIndex
            ? {
                ...msg,
                status: MESSAGE_STATUS.ERROR
              }
            : msg
        )
      );
      setNotice({
        type: targetMessage.role === 'user' ? 'user-save-error' : 'assistant-save-error',
        text:
          targetMessage.role === 'user'
            ? t('chat.noticeUserSaveError')
            : t('chat.noticeAssistantSaveError')
      });
    }
  }, [sessionId, t]);

  // Handle Enter key to send message (with IME composition guard for Japanese input).
  // onKeyDown is used instead of deprecated onKeyPress; isComposing check prevents
  // sending when Japanese IME is confirming character composition.
  const handleTextSelectionRef = useRef(handleTextSelection);
  handleTextSelectionRef.current = handleTextSelection;

  const handleKeyDown = (e) => {
    // Decision lives in utils/imeSubmit so the test exercises the real rule
    // rather than a copy of it.
    if (!shouldSubmitOnEnter(e)) return;
    e.preventDefault();
    handleSend();
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl" style={{ border: '1px solid var(--border)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)' }}>
      <div className="rounded-t-xl px-5 py-4" style={{ background: 'var(--primary)', color: 'var(--text-on-primary)', borderBottom: '1px solid var(--primary-strong)' }}>
        <h3 className="text-lg font-bold tracking-wide"><FuriganaText text={t('chat.header')} /></h3>
        <p className="mt-1 text-xs" style={{ color: 'rgba(253, 251, 246, 0.8)' }}>
          <FuriganaText text={t('chat.headerSubtitle')} />
        </p>
      </div>

      {notice && (
        <div className="border-b px-5 py-3 text-xs flex items-start justify-between gap-3" style={{ borderColor: 'var(--border)', background: 'rgba(109, 69, 23, 0.08)', color: 'var(--warning)' }}>
          <span><FuriganaText text={notice.text} /></span>
          <button
            onClick={() => setNotice(null)}
            className="rounded-full px-3 py-1 text-[10px] font-semibold transition-colors"
            style={{ background: 'rgba(109, 69, 23, 0.12)', color: 'var(--warning)' }}
          >
            <FuriganaText text={t('common.close')} />
          </button>
        </div>
      )}



      <div ref={messageLogRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5 pr-6 min-h-0" role="log" aria-label={t('chat.log.ariaLabel')}>
        {isHistoryLoading && (
          <div className="flex justify-center">
            <div className="rounded-lg px-3 py-1 text-[11px] font-semibold" style={{ background: 'var(--bg-muted)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
              <FuriganaText text={t('chat.loadingHistory')} />
            </div>
          </div>
        )}

        {loadError && (
          <div className="rounded-xl border px-4 py-3 text-xs shadow-sm" style={{ borderColor: 'var(--border)', background: 'rgba(109, 69, 23, 0.08)', color: 'var(--warning)' }}>
            <FuriganaText text={t('chat.loadHistoryError')} />
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={msg.id ?? idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
              {/* 头像 */}
              <div className={`flex items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {/* Decorative role glyph (single char): aria-hidden + no furigana — an
                    isolated single kanji has no reliable reading (先→さき, 生→なま), so per
                    correctness-first it is not annotated (same as the Dashboard monogram). */}
                <div aria-hidden="true" className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold" style={{ background: msg.role === 'user' ? 'var(--warm)' : 'var(--primary)', color: 'var(--text-on-primary)', boxShadow: 'var(--shadow-sm)' }}>
                  {msg.role === 'user' ? (studentProfile?.name?.[0] || studentProfile?.username?.[0] || t('chat.avatarStudentFallback')) : t('chat.avatarTeacher')}
                </div>

                {/* 消息气泡 */}
                <div className="rounded-lg px-4 py-2"
                style={{
                  ...(msg.role === 'user'
                    ? { background: 'var(--primary)', color: 'var(--text-on-primary)', boxShadow: 'var(--shadow-sm)' }
                    : msg.isError
                    ? { background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)' }
                    : { background: 'var(--bg-surface)', border: '1px solid var(--border)', color: settings?.textColor || 'var(--text)' })
                }}>
                  <div
                    className="text-sm whitespace-pre-wrap select-text cursor-text"
                    data-message-index={idx}
                    onMouseUp={() => handleTextSelection(idx)}
                    style={{
                      fontFamily: resolveFontFamily(settings.fontFamily),
                      fontSize: `${settings?.fontSize || 22}px`,
                      letterSpacing: `${settings.letterSpacing || 0.05}em`,
                      padding: '4px',
                      borderRadius: '4px'
                    }}
                  >
                    {msg.isGreeting && msg.identityName && msg.contentMarked
                      ? renderMessageWithBareIdentity(msg.contentMarked, msg.identityName, highlightedText[idx])
                      : renderHighlightedText(msg.content, highlightedText[idx], true)}
                  </div>

                  {/* ハイライト管理ボタン */}
                  {highlightedText[idx] && highlightedText[idx].length > 0 && (
                    <div className="mt-3 space-y-1">
                      <div className="flex flex-wrap gap-2">
                        {highlightedText[idx].map((text, hIdx) => (
                          <button
                            key={hIdx}
                            onClick={() => removeHighlight(idx, text)}
                            className="flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold transition-colors"
                            style={{ background: 'rgba(109, 69, 23, 0.2)', color: 'var(--warning)' }}
                            title={fa(t('chat.removeHighlight'))}
                          >
                            <span className="max-w-[200px] truncate">
                              {/* Preserve identity subranges even when a selection includes surrounding chrome
                                  (小鳥遊さん / こんにちは、小鳥遊さん): the source is annotated once,
                                  but every part of the participant name stays bare. */}
                              <FuriganaFragment
                                source={msg.content}
                                text={text}
                                bareRanges={msg.isGreeting && msg.identityName && msg.contentMarked
                                  ? getIdentityProjection(msg.contentMarked, msg.identityName).identityRanges
                                  : []}
                              />
                            </span>
                            <span>×</span>
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => clearAllHighlights(idx)}
                        className="rounded-full px-3 py-1 text-[11px] font-semibold transition-colors"
                        style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
                      >
                        <FuriganaText text={t('chat.clearAllHighlights')} />
                      </button>
                    </div>
                  )}
                  {msg.status === MESSAGE_STATUS.PENDING && (
                    <div className="mt-2 text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>
                      <FuriganaText text={t('chat.saving')} />
                    </div>
                  )}
                  {msg.status === MESSAGE_STATUS.ERROR && (
                    <div className="mt-2 flex items-center gap-2 text-[10px] font-semibold" style={{ color: 'var(--danger)' }}>
                      <span><FuriganaText text={t('chat.saveFailed')} /></span>
                      <button
                        onClick={() => retryPersistence(idx)}
                        className="rounded-full border px-2 py-1 text-[10px] font-semibold transition-colors"
                        style={{ borderColor: 'var(--danger)', background: 'var(--bg-surface)', color: 'var(--danger)' }}
                      >
                        <FuriganaText text={t('chat.retry')} />
                      </button>
                    </div>
                  )}
                  {/* 音声プレーヤー */}
                  {msg.role === 'user' && userRecordingAudioUrls[idx] && (
                    <div className="mt-2">
                      <div className="text-xs mb-1 opacity-70"><FuriganaText text={t('chat.yourVoice')} /></div>
                      <AudioPlayer
                        audioUrl={userRecordingAudioUrls[idx]}
                        onEnded={() => console.log('ユーザー録音再生完了')}
                      />
                    </div>
                  )}
                  {msg.role === 'assistant' && aiResponseAudioUrls[idx] && (
                    <div className="mt-2">
                      <div className="text-xs mb-1 opacity-70"><FuriganaText text={t('chat.teacherVoice')} /></div>
                      <AudioPlayer
                        audioUrl={aiResponseAudioUrls[idx]}
                        onEnded={() => console.log('AI音声再生完了')}
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* タイムスタンプ — バブル外・下部 */}
              <div className={`mt-1 text-[9px] ${msg.role === 'user' ? 'text-right pr-11' : 'pl-11'}`} style={{ color: 'var(--text-muted)' }}>
                {formatTimestamp(msg.timestamp)}
              </div>
            </div>
          </div>
        ))}

        {/* 正在输入指示器 */}
        {isTyping && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-lg px-4 py-2" style={{ background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
              <div className="flex gap-1">
                <div className="h-2 w-2 animate-bounce rounded-full bg-[var(--primary)]" style={{ animationDelay: '0ms' }}></div>
                <div className="h-2 w-2 animate-bounce rounded-full bg-[var(--primary)]" style={{ animationDelay: '150ms' }}></div>
                <div className="h-2 w-2 animate-bounce rounded-full bg-[var(--primary)]" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('chat.thinking')} /></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="rounded-b-[28px] border-t px-5 py-5" style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}>
        <div className="flex gap-3">
          {settings.voiceInputEnabled && (
            <button
              onClick={toggleVoiceInput}
              disabled={isTyping}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                isRecording
                  ? 'shadow-inner animate-pulse'
                  : 'border-2 shadow-sm'
              }`}
              style={isRecording
                ? { background: 'var(--danger)', color: 'var(--text-on-primary)' }
                : { borderColor: 'var(--border)', background: 'var(--bg-surface)', color: 'var(--primary)' }
              }
              title={fa(t('chat.voiceInputTitle'))}
            >
              <FuriganaText text={isRecording ? t('chat.recording') : '🎤'} />
            </button>
          )}

          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={fa(isRecording ? t('chat.placeholderRecording') : t('chat.placeholderInput'))}
            disabled={isTyping || isRecording}
            // min-w-0 is load-bearing: a flex item defaults to min-width:auto,
            // so the input refuses to shrink below its content and pushes the
            // send button off the edge. Visible as soon as the chat column got
            // narrower for tablet portrait.
            className="flex-1 min-w-0 rounded-full border-2 px-5 py-2 text-sm shadow-sm transition-all focus:outline-none focus:ring-2"
            style={{
              fontFamily: resolveFontFamily(settings.fontFamily),
              fontSize: `${settings?.fontSize || 22}px`,
              letterSpacing: `${settings.letterSpacing || 0.05}em`,
              color: settings.textColor,
              borderColor: 'var(--border)',
              background: 'var(--bg-surface)',
              '--tw-ring-color': 'rgba(61, 90, 110, 0.15)',
            }}
          />

          <button
            onClick={handleSend}
            disabled={isTyping || !inputText.trim() || isRecording}
            className="rounded-full px-4 py-2 text-xs font-semibold shadow-md transition-transform hover:scale-[1.02] disabled:scale-100 whitespace-nowrap flex-shrink-0"
            style={{ background: 'var(--primary)', color: 'var(--text-on-primary)' }}
          >
            <FuriganaText text={t('chat.send')} />
          </button>
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={() => handleSend(t('chat.dontKnow.request'), { assistanceRequest: true })}
            disabled={isTyping || isRecording || !sessionId}
            aria-label={t('chat.dontKnow.aria')}
            title={fa(t('chat.dontKnow.aria'))}
            className="w-full rounded-full px-4 text-sm font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minHeight: 44, background: 'var(--primary-bg)', color: 'var(--primary)', border: '2px solid var(--primary)' }}
          >
            <FuriganaText text={t('chat.dontKnow.label')} />
          </button>
        </div>

        {quickSuggestions.length > 0 && (
          /* Compact footprint: the footer does not scroll and is a flex sibling of the
             message log, so every line here is taken away from the visible conversation.
             The standing heading was dropped and its text now names the group for screen
             readers; the hint stays permanently visible but is a single short line.
             Candidates are never truncated — a student has to READ one to choose it. */
          <div className="mt-3" role="group" aria-label={t('chat.replySuggestions.label')}>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              <FuriganaText text={t('chat.replySuggestions.hint')} />
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {quickSuggestions.map((suggestion) => {
                const isSelected = inputText === suggestion.value;
                return (
                  <button
                    type="button"
                    key={suggestion.id}
                    onClick={() => setInputText(suggestion.value)}
                    aria-pressed={isSelected}
                    disabled={isTyping || isRecording}
                    className="flex-1 basis-64 rounded-2xl border px-4 py-2 text-left font-medium transition-colors disabled:opacity-50"
                    style={{
                      minHeight: 44,
                      // Match the message bubbles and the input box: this is reading-critical
                      // text, so it must follow the student's own font/size/spacing settings.
                      fontFamily: resolveFontFamily(settings?.fontFamily),
                      fontSize: `${settings?.fontSize || 22}px`,
                      letterSpacing: `${settings?.letterSpacing || 0.05}em`,
                      borderColor: isSelected ? 'var(--primary)' : 'var(--border)',
                      background: isSelected ? 'var(--primary-bg)' : 'var(--bg-surface)',
                      color: 'var(--primary)',
                    }}
                  >
                    <FuriganaText text={suggestion.label} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {typeof onSupportLevelChange === 'function' && (
          <div className="mt-3">
            <SupportLevelSelector
              value={supportLevel ?? 2}
              onChange={onSupportLevelChange}
              words={supportWords}
              effective={effectiveSupportLevel}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatInterface;
